import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  FormControlStatus,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IonAccordion, IonAccordionGroup, IonCheckbox, IonItem, IonLabel, IonText } from '@ionic/angular';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { addYears } from 'date-fns';
import { distinctUntilChanged, filter, first, map, Observable, startWith, Subscription, tap } from 'rxjs';

import { NtyUniversalCreateComponent, UserListModel } from '@ninety/mobile/platform/api';
import {
  DescriptionComponent,
  NtyDatePickerComponent,
  NtyFormErrorComponent,
  TitleComponent,
  TypeAheadItemModel,
} from '@ninety/mobile/platform/components';
import { CurrentUserSelectors, LanguageSelectors, UniversalCreateActions } from '@ninety/mobile/platform/state';

import { DiscussionRepetitionFrequency } from '../../models/discussion.models';
import { CreateDiscussionDto } from '../../models/dto/create-discussion.dto';
import { CreateDiscussionForm } from '../../models/form/create-discussion-form.model';
import { CreateDiscussionFormWatcher } from '../../services/create-discussion-form-watcher/create-discussion-form-watcher.service';
import { CreateDiscussionComponentActions } from '../../state/actions';
import { selectDiscussionUniversalCreateButtonText } from '../../state/selectors';
import { DiscussionStateModule } from '../../state/state';
import { DiscussionParticipantSelectComponent } from '../forms/discussion-participant-select/discussion-participant-select.component';
import { DiscussionSeatSelectComponent } from '../forms/discussion-seat-select/discussion-seat-select.component';

@Component({
  selector: 'nty-create-discussion',
  imports: [
    IonAccordion,
    IonAccordionGroup,
    IonCheckbox,
    IonItem,
    IonLabel,
    IonText,
    CommonModule,
    ReactiveFormsModule,
    LetDirective,
    DiscussionStateModule,
    DescriptionComponent,
    TitleComponent,
    DiscussionParticipantSelectComponent,
    DiscussionSeatSelectComponent,
    NtyDatePickerComponent,
    NtyFormErrorComponent,
  ],
  providers: [CreateDiscussionFormWatcher],
  templateUrl: './create-discussion.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./create-discussion.component.scss'],
})
export class CreateDiscussionComponent implements OnInit, OnDestroy, NtyUniversalCreateComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly store = inject(Store);
  private readonly watcher = inject(CreateDiscussionFormWatcher);

  minDueDate = new Date().toISOString();
  maxDueDate = addYears(new Date(), 3).toISOString();
  isSubmitted = false;

  readonly form = this.fb.group<CreateDiscussionForm>({
    title: this.fb.control('', [Validators.required, Validators.maxLength(80)]),
    description: this.fb.control(''),
    managerId: this.fb.control('', [Validators.required]),
    manageeId: this.fb.control('', [Validators.required]),
    manageeSeatHolderId: this.fb.control('', [Validators.required]),
    dueDate: this.fb.control(new Date().toISOString(), [Validators.required]),
    periodStartDate: this.fb.control(new Date().toISOString(), [Validators.required]),
    periodEndDate: this.fb.control(new Date().toISOString(), [Validators.required]),
    canEvaluateSelf: this.fb.control(true),
    canEvaluateManager: this.fb.control(true),
    holdingMeeting: this.fb.control(true),
    revealAnswersPriorToMeeting: this.fb.control(true),
    createTodos: this.fb.control(false),
    repeat: this.fb.control(DiscussionRepetitionFrequency.none),
  });

  managerUserOptions$!: Observable<UserListModel[]>;
  manageeUserOptions$!: Observable<TypeAheadItemModel[]>;
  manageeSeatOptions$!: Observable<TypeAheadItemModel[]>;

  language$ = this.store.select(LanguageSelectors.discussionLanguage);

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(this.watcher.watchDiscussionForm(this.form));

    this.subscriptions.add(
      this.store
        .select(CurrentUserSelectors.selectUser)
        .pipe(
          filter(user => !!user?._id),
          tap(user => this.form.controls.managerId.setValue(user?._id as string)),
          first()
        )
        .subscribe()
    );

    this.manageeSeatOptions$ = this.watcher.getManageeSeatOptions(this.form);
    this.managerUserOptions$ = this.watcher.getManagerUserOptions(this.form);
    this.manageeUserOptions$ = this.watcher.getManageeUserOptions(this.form);

    this.subscriptions.add(
      this.store
        .select(selectDiscussionUniversalCreateButtonText)
        .pipe(distinctUntilChanged())
        .subscribe(createButtonText =>
          this.store.dispatch(UniversalCreateActions.createButtonText({ createButtonText }))
        )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  create() {
    if (this.form.invalid) {
      this.isSubmitted = true;
      return;
    }
    this.store.dispatch(
      CreateDiscussionComponentActions.createDiscussionClicked({ request: new CreateDiscussionDto(this.form) })
    );
  }

  protected isOptional(formControl: AbstractControl): boolean {
    return !formControl.hasValidator(Validators.required) && !formControl.disabled;
  }

  protected hasRequiredError(controlName: string): boolean {
    return this.form.get(controlName)?.hasError('required') || false;
  }
}
